import debounce from 'debounce-promise';
import { useCallback, useMemo, useRef, useState } from 'react';
import { IValidationResult, Validation } from '../../validators/validator';

export interface IValidator<T> {
  errorMessage: string;
  handleEvent: (value: T, skipValidation?: boolean) => Promise<void>;
  reset: () => void;
  results: IValidationResult[];
  invalid: boolean;
  validated: boolean;
  validating: boolean;
  validate: () => Promise<boolean>;
  value: T;
}

export interface IUseValidatorConfig {
  debounceMs?: number;
  validated?: boolean;
}

const DEFAULT_DEBOUNCE_MS = 400;
const DEFAULT_VALIDATING = false;
const DEFAULT_VALIDATED = false;
const DEFAULT_RESULTS: IValidationResult[] = [];

export const useValidator = <T>(
  initialValue: T,
  validation: Validation<T>,
  { debounceMs = DEFAULT_DEBOUNCE_MS, validated: initialValidated = DEFAULT_VALIDATED }: IUseValidatorConfig = {}
): IValidator<T> => {
  const [value, setValue] = useState<T>(initialValue);
  const [validating, setValidating] = useState(DEFAULT_VALIDATING);
  const [results, setResults] = useState<IValidationResult[]>(DEFAULT_RESULTS);

  const validatedRef = useRef(initialValidated);
  const lastValidatedValueRef = useRef<T>(undefined);

  const handleValidation = useCallback(
    async (newValue: T) => {
      setValidating(true);

      try {
        const newResults = await validation.results(newValue);
        validatedRef.current = true;
        setResults(newResults);

        lastValidatedValueRef.current = newValue;

        return newResults.every((x) => x.valid);
      } finally {
        setValidating(false);
      }
    },
    [validation]
  );

  const debouncedHandleValidation = useMemo(() => debounce(handleValidation, debounceMs), [debounceMs]);

  const handleEvent = async (newValue: T, skipValidation?: boolean) => {
    if (lastValidatedValueRef.current !== newValue || !validatedRef.current) {
      setValue(newValue);

      if (skipValidation) {
        return;
      }

      await debouncedHandleValidation(newValue);
    }
  };

  const invalidResult = results.find((x) => !x.valid);
  const invalid = validatedRef.current && !!invalidResult;
  const errorMessage = invalidResult?.message ?? '';

  const onReset = () => {
    validatedRef.current = initialValidated;
    setValue(initialValue);
    setValidating(DEFAULT_VALIDATING);
    setResults(DEFAULT_RESULTS);
    lastValidatedValueRef.current = undefined;
  };

  return {
    errorMessage,
    handleEvent,
    reset: onReset,
    results,
    invalid,
    validate: async () => handleValidation(value),
    validated: validatedRef.current,
    validating,
    value
  };
};
